import { BrowserDetector } from "../browser/BrowserDetector";

export interface ISet<TValue> {
	forEach(callbackfn: (value: TValue) => void): void;

	add(value: TValue): ISet<TValue>;

	delete(value: TValue): ISet<TValue>;

	has(value: TValue): boolean;

	[Symbol.iterator](): IterableIterator<TValue>;

	clear(): void;

	readonly size: number;
}

export class NativeSet<TValue> implements ISet<TValue> {
	private readonly set: Set<TValue>;

	constructor(existingSet?: Set<TValue> | TValue[] | null) {
		this.set = existingSet ? new Set<TValue>(existingSet) : new Set<TValue>();
	}

	get size(): number {
		return this.set.size;
	}

	forEach(callback: (value: TValue) => void): void {
		this.set.forEach(e => callback(e));
	}

	add(value: TValue): ISet<TValue> {
		this.set.add(value);
		return this;
	}

	delete(value: TValue): ISet<TValue> {
		this.set.delete(value);
		return this;
	}

	has(value: TValue): boolean {
		return this.set.has(value);
	}

	clear(): void {
		this.set.clear();
	}

	[Symbol.iterator](): IterableIterator<TValue> {
		return this.set[Symbol.iterator]();
	}
}

export class BasicSet<TValue> implements ISet<TValue> {
	private readonly array: TValue[];

	constructor(existingArray?: TValue[] | null) {
		this.array = existingArray ?? [];
	}

	get size(): number {
		return this.array.length;
	}

	forEach(callback: (value: TValue) => void): void {
		this.array.forEach(e => callback(e));
	}

	add(value: TValue): ISet<TValue> {
		if (!this.has(value)) this.array.push(value);
		return this;
	}

	delete(value: TValue): ISet<TValue> {
		const index = this.array.indexOf(value);

		if (index !== -1) {
			this.array.splice(index, 1);
		}

		return this;
	}

	has(value: TValue): boolean {
		return this.array.includes(value);
	}

	clear(): void {
		this.array.splice(0, this.array.length);
	}

	[Symbol.iterator](): IterableIterator<TValue> {
		return this.array[Symbol.iterator]();
	}
}

export class SetFactory {
	public static createNew<TValue>(): ISet<TValue> {
		const isShittyBrowser = BrowserDetector.isThisALegacyBrowser();
		return isShittyBrowser ? new BasicSet<TValue>() : new NativeSet<TValue>();
	}

	public static create<TValue>(iterable: TValue[]): ISet<TValue> {
		const isShittyBrowser = BrowserDetector.isThisALegacyBrowser();
		return isShittyBrowser ? new BasicSet<TValue>(iterable) : new NativeSet<TValue>(iterable);
	}
}
